import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Compass } from "lucide-react";
import { IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, SectionHeading, Reveal } from "@/components/Section";
import { ValuesGrid } from "@/components/ValuesGrid";
import { Testimonials } from "@/components/Testimonials";
import { TalkCta } from "@/components/TalkCta";

const TILES = ["tile-sky", "tile-sun", "tile-blue", "tile-peach", "tile-mint", "tile-coral"];
const PILLARS = [
  { id: "our-mission", icon: Target, title: "Our Mission", key: "mission", tile: "tile-blue corner-tr", light: true },
  { id: "our-vision", icon: Eye, title: "Our Vision", key: "vision", tile: "tile-sun corner-bl", light: false },
  { id: "our-purpose", icon: Compass, title: "Our Purpose", key: "purpose", tile: "tile-sky corner-tr", light: false },
];

const Pillars = () => {
  const t = useT();
  const { MISSION } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="why-pillars">
      <div className="container-x">
        <SectionHeading eyebrow={t("Mission, vision & purpose")} title={t("What drives everything we do")} />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div className={`${p.tile} p-8 h-full`} data-testid={`why-pillar-${p.id}`}>
                <span className={`w-12 h-12 rounded-md flex items-center justify-center ${p.light ? "bg-white/20 text-white" : "bg-white text-[var(--blue)]"}`}><p.icon size={22} /></span>
                <h3 className={`mt-6 text-2xl font-semibold ${p.light ? "!text-white" : "!text-[var(--ink)]"}`}>{t(p.title)}</h3>
                <p className={`mt-3 leading-relaxed ${p.light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{MISSION[p.key]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function WhyUs() {
  const t = useT();
  const { WHY } = useSite();
  return (
    <>
      <PageIntro eyebrow={t("Why Aid in Care")} title={t("Care that is personal,")} accent={t("flexible and reliable.")} text={t("We're more than a provider. We take the time to understand you, match you with the right people and keep communication open — so you always feel in control.")} image={IMAGES.transport}>
        <div className="mt-8"><Link to="/contact" className="btn btn-coral" data-testid="why-enquire">{t("Enquire About Support")} <ArrowRight size={16} /></Link></div>
      </PageIntro>
      <Pillars />
      <section className="py-16 lg:py-24 bg-white" data-testid="why-sections">
        <div className="container-x">
          <SectionHeading eyebrow={t("What sets us apart")} title={t("Six reasons families choose us")} />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map((w, i) => {
              const light = TILES[i] === "tile-blue" || TILES[i] === "tile-coral";
              return (
                <Reveal key={i} delay={(i % 3) * 0.08} className="h-full">
                  <div className={`card card-hover overflow-hidden h-full flex flex-col ${TILES[i]} ${i % 2 ? "!rounded-none corner-bl" : ""}`} data-testid={`why-card-${i}`}>
                    <img src={w.image} alt="" className="h-44 w-full object-cover" />
                    <div className="p-7 flex-1">
                      <span className={`text-xs font-semibold ${light ? "text-white/80" : "text-[var(--blue)]"}`}>0{i + 1}</span>
                      <h3 className={`mt-2 text-xl font-semibold leading-snug ${light ? "!text-white" : "!text-[var(--ink)]"}`}>{w.title}</h3>
                      <p className={`mt-3 text-[15px] leading-relaxed ${light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{w.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-16 lg:py-24 bg-[var(--blue-tint)]">
        <div className="container-x">
          <SectionHeading eyebrow={t("Our values")} title={t("What you can expect from every one of us")} />
          <div className="mt-10"><ValuesGrid /></div>
        </div>
      </section>
      <Testimonials />
      <TalkCta sourcePage="why-us" />
    </>
  );
}
